import { call } from '@advanza/api'
import { S3_PUBLIC } from '@advanza/constants'
import { Col, Row } from '@advanza/grid'
import { Button, Divider, HoverText, Icon, LoadingDots, PreIcon } from '@advanza/ui'
import NiceModal from '@ebay/nice-modal-react'
import { fetchProvider } from 'actions/providers'
import ChurnReasonEditor from 'components/cancellations/ChurnReasonEditor'
import { getActiveStatus } from 'components/providers/_providerActiveStatus'
import CalendlyOverview from 'components/providers/CalendlyOverview'
import DemandUserButton from 'components/providers/DemandUserButton'
import DemoRequestButton from 'components/providers/DemoRequestButton'
import DuplicateProviderBadge from 'components/providers/DuplicateProviderBadge'
import GoLiveRequirementsButton from 'components/providers/goLive/GoLiveRequirementsButton'
import HealthScoreHeaderBadge from 'components/providers/healthScore/HealthScoreHeaderBadge'
import ProspectLabel from 'components/providers/ProspectLabel'
import ProviderManualPause from 'components/providers/ProviderManualPause'
import WebinarOnboardingStatus from 'components/providers/WebinarOnboardingStatus'
import { getChurnReasonDisplayText } from 'components/reports/common/constants'
import FormattedTime from 'components/ui/FormattedTime'
import auroraStyle from 'css/aurora.module.css'
import { utcToLocal } from 'date'
import { addWeeks, format } from 'date-fns'
import { useAdminUser } from 'hooks/adminUsersHooks'
import { useAppDispatch } from 'hooks/hooks'
import { useIsTest } from 'hooks/miscHooks'
import { useProvider, useProviderStatsSettings } from 'hooks/providerHooks'
import { Provider } from 'interfaces/Providers'
import { hasAccess, isSuperUser } from 'misc/user'
import { useState } from 'react'

interface ProviderStatusProps {
    providerId: number
    glowing?: boolean
}

interface ExtendedProvider extends Omit<Provider, 'active_free_trial'> {
    status: number
    claimed?: boolean
    bans?: unknown[]
    responseTimeP80?: number
    isSale?: boolean
    cityName?: string
    slow_response?: boolean
    office_labels?: Array<{ type: string; _joinData?: { user_id?: number } }>
    latestChurnReason?: number | null
    latestChurnReasonId?: number | null
    payment_info?: unknown
    active_free_trial?: {
        free_lead_id: number
        day_limit_reached?: boolean
    } | null
}

const ProviderStatus = ({ providerId, glowing = false }: ProviderStatusProps) => {
    const isTest = useIsTest()
    const dispatch = useAppDispatch()
    const { provider, user, leadSettings } = useProvider(providerId)
    const { onRemoveSlowLabel, onResetFreeTrialDayLimit, isSaving } =
        useProviderStatsSettings(providerId)
    const extendedProvider = provider as ExtendedProvider
    const showPaymentInfoWarning = !extendedProvider.payment_info && provider?.isSigned
    const activeStatus = getActiveStatus(extendedProvider)
    const isSlow = extendedProvider?.slow_response
    const isAdmin = isSuperUser() || hasAccess('providers', 'w')
    const onboardingOwnerLabel = extendedProvider?.office_labels?.find(
        ({ type }) => type === 'onboarding_owner',
    )
    const { user: onboardingOwnerUser } = useAdminUser(onboardingOwnerLabel?._joinData?.user_id)

    const handleEditChurnReason = () => {
        if (extendedProvider.latestChurnReasonId && extendedProvider.latestChurnReason !== null) {
            NiceModal.show(ChurnReasonEditor, {
                premiumCancelRequestId: extendedProvider.latestChurnReasonId,
                currentReason: extendedProvider.latestChurnReason,
                providerId: providerId,
                onSave: () => {
                    dispatch(fetchProvider(providerId, true))
                },
            })
        }
    }

    if (!provider || !user) {
        return null
    }

    return (
        <div>
            <Row className="flex-nowrap">
                <Col x>
                    <div className={glowing ? auroraStyle.aurora : ''} style={{ borderRadius: 8 }}>
                        <div className="bg-neutral-300 w-[100px] h-[100px] relative rounded-lg overflow-hidden">
                            <div
                                className="w-full h-full absolute left-0 top-0 opacity-50"
                                style={{
                                    background: `url(${S3_PUBLIC()}users/${user.id}/${
                                        user.avatar_filename
                                    }) no-repeat center center /cover`,
                                }}
                            />
                            <div
                                className="absolute left-0 top-0 w-full h-full flex m-auto text-center"
                                style={{ backgroundColor: activeStatus.backgroundColor }}>
                                <Icon name={activeStatus.icon} white />
                            </div>
                        </div>
                    </div>
                </Col>
                <Col x>
                    <h3>
                        {extendedProvider.business_name}{' '}
                        {extendedProvider.claimed && <Icon name="verified_user" primColor />}
                        {(extendedProvider.bans || []).length > 0 && (
                            <HoverText trigger={<Icon name="coronavirus" red />}>
                                Quarantined
                            </HoverText>
                        )}
                        {extendedProvider.responseTimeP80 &&
                            extendedProvider.responseTimeP80 > 24 && (
                                <Icon
                                    title={
                                        'response time of ' +
                                        extendedProvider.responseTimeP80 +
                                        ' hours (p80)'
                                    }
                                    className="text-blue-400"
                                    name="elderly_woman"
                                />
                            )}
                        {extendedProvider.isSale ? (
                            <Icon
                                title="Is Sale"
                                className="text-yellow-600"
                                name="point_of_sale"
                            />
                        ) : null}
                        <DuplicateProviderBadge
                            duplicateProfileId={extendedProvider.duplicateProfile ?? null}
                            currentProviderId={providerId}
                        />
                        <DemandUserButton
                            demandUserRequestId={extendedProvider.demandUserRequestId ?? null}
                        />
                    </h3>
                    <PreIcon icon="place" fontSize={18}>
                        {extendedProvider.cityName}
                    </PreIcon>
                    <div style={{ color: activeStatus.color }}>
                        {activeStatus.title}
                        {activeStatus.title === 'Free Limit reached' &&
                            leadSettings?.free_month_limit_reached && (
                                <em className="text-dark ml-1">
                                    until&nbsp;
                                    {format(
                                        addWeeks(
                                            new Date(leadSettings.free_month_limit_reached),
                                            6,
                                        ),
                                        'd MMMM yyyy',
                                    )}
                                </em>
                            )}
                    </div>
                    {leadSettings?.credit_limit_reached && (
                        <div className="text-md text-[#ff0000]">Credit limit reached</div>
                    )}
                    {isAdmin &&
                        (activeStatus.title === 'Unsubscribed' ||
                            activeStatus.title === 'Churned') && (
                            <div className="flex items-center gap-2 mt-1">
                                {extendedProvider.latestChurnReason !== null &&
                                extendedProvider.latestChurnReason !== undefined ? (
                                    <>
                                        <span className="text-md text-neutral-600">
                                            Reason:{' '}
                                            {getChurnReasonDisplayText(
                                                extendedProvider.latestChurnReason,
                                            )}
                                        </span>
                                        {extendedProvider.latestChurnReasonId && (
                                            <Icon
                                                name="edit"
                                                className="cursor-pointer text-base text-blue-500"
                                                onClick={handleEditChurnReason}
                                                title="Edit churn reason"
                                            />
                                        )}
                                    </>
                                ) : (
                                    <span className="text-md text-neutral-400 italic">
                                        No churn reason recorded
                                    </span>
                                )}
                            </div>
                        )}
                    {extendedProvider.scheduledTermination && (
                        <div className="mt-1 flex items-center gap-1 text-md font-medium text-tertiary-500">
                            <Icon name="event_busy" size="sm" orange />
                            Terminates on{' '}
                            {utcToLocal(
                                extendedProvider.scheduledTermination.termination_date,
                                'd MMMM yyyy',
                            )}
                        </div>
                    )}
                    {provider.isSigned && <ProviderManualPause providerId={providerId} />}
                    {showPaymentInfoWarning && (
                        <>
                            <Divider />
                            <div className="text-red-600">No payment info!</div>
                        </>
                    )}
                    {isTest &&
                        provider.isFreeTrial &&
                        extendedProvider.active_free_trial?.day_limit_reached && (
                            <Button
                                name="link"
                                disabled={isSaving}
                                onClick={onResetFreeTrialDayLimit}>
                                {isSaving ? <LoadingDots /> : ' Reset free trial day limit'}
                            </Button>
                        )}
                    {isSlow && (
                        <Row middle="xs">
                            <Col x>
                                <i className="text-[21px]">🐢</i>
                            </Col>
                            <Col x>Slow response </Col>
                            <Col x>
                                {' '}
                                <Button name="link" disabled={isSaving} onClick={onRemoveSlowLabel}>
                                    {isSaving ? <LoadingDots /> : ' Reset'}
                                </Button>
                            </Col>
                        </Row>
                    )}
                    <CalendlyOverview providerId={provider.service_provider_id} />
                    <WebinarOnboardingStatus provider={extendedProvider} />
                    <PitchedStatus
                        providerId={providerId}
                        pitchedAt={leadSettings?.pitched_at ?? null}
                        settingId={leadSettings?.setting_id}
                        canEdit={isAdmin}
                    />
                    <GoLiveRequirementsButton
                        providerId={providerId}
                        status={extendedProvider.status}
                    />
                    <Divider s />
                    <DemoRequestButton providerId={provider.service_provider_id} />
                    <Divider s />
                    <ProspectLabel
                        providerId={provider.service_provider_id}
                        provider={extendedProvider}
                        fontSize={18}
                        emptyValue=""
                    />
                </Col>
                {provider.isSigned && (
                    <Col x className="ml-auto">
                        <HealthScoreHeaderBadge providerId={providerId} />
                    </Col>
                )}
            </Row>
            {onboardingOwnerUser && (
                <>
                    <Divider s />
                    <h6>
                        Onboarding owner: {onboardingOwnerUser.first_name}{' '}
                        {onboardingOwnerUser.last_name}
                    </h6>
                </>
            )}
        </div>
    )
}

interface PitchedStatusProps {
    providerId: number
    pitchedAt: string | null
    settingId?: number
    canEdit: boolean
}

const PitchedStatus = ({ providerId, pitchedAt, settingId, canEdit }: PitchedStatusProps) => {
    const dispatch = useAppDispatch()
    const [isSaving, setIsSaving] = useState(false)

    const savePitchedAt = (pitchedAtValue: string | null) => {
        if (!settingId || isSaving) {
            return
        }
        setIsSaving(true)
        call(`office/providers/save-lead-settings/${settingId}`, {
            json: {
                service_provider_id: providerId,
                pitched_at: pitchedAtValue,
            },
        })
            .then(() => dispatch(fetchProvider(providerId, true)))
            .finally(() => setIsSaving(false))
    }

    const onSetPitched = () => savePitchedAt(new Date().toISOString())

    const onClearPitched = () => savePitchedAt(null)

    return (
        <div className="mt-1">
            <Row middle="xs">
                <Col x>
                    <span className="text-md font-medium">Pitched:</span>
                </Col>
                <Col x>
                    {pitchedAt ? (
                        <>
                            <FormattedTime date={pitchedAt} format="dd-MM-yyyy HH:mm" />
                            {canEdit && (
                                <Button
                                    name="link"
                                    className="ml-1 text-sm"
                                    disabled={isSaving}
                                    onClick={onClearPitched}>
                                    {isSaving ? <LoadingDots /> : 'Clear'}
                                </Button>
                            )}
                        </>
                    ) : (
                        <>
                            <span className="text-md text-neutral-400">Not pitched</span>
                            {canEdit && (
                                <Button
                                    name="link"
                                    className="ml-1"
                                    disabled={isSaving}
                                    onClick={onSetPitched}>
                                    {isSaving ? <LoadingDots /> : 'Set Pitched'}
                                </Button>
                            )}
                        </>
                    )}
                </Col>
            </Row>
        </div>
    )
}

export default ProviderStatus
